import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ArrowRight, MapPin, Phone, Star, Clock, Laptop2 } from "lucide-react";
import SaleTag from "./SaleTag";
import Reveal from "./Reveal";

const ModernHero = () => {
  return (
    <section id="home" className="relative py-16 md:py-24 lg:py-28 overflow-hidden bg-aurora">
      {/* Leiterbahn-Muster aus dem Logo */}
      <div className="absolute inset-0 bg-circuit opacity-60 pointer-events-none" aria-hidden="true" />

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-flame/5 rounded-full blur-3xl" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-12 lg:gap-20 lg:grid-cols-2">

          {/* Left Content */}
          <div className="space-y-8 lg:space-y-10">

            {/* Service Badge */}
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full bg-background/80 backdrop-blur px-5 py-2.5 text-sm font-bold text-navy border border-navy/10 shadow-soft">
                <span className="inline-flex rounded-full h-2 w-2 bg-flame" aria-hidden="true"></span>
                IT-Support • Beratung • Schulung
              </div>
            </Reveal>

            {/* Main Headline - Much Larger */}
            <div className="space-y-6">
              <Reveal delay={80}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-navy leading-[1.05]">
                  Technik-
                  <br />
                  Probleme?
                  <span className="block mt-3 text-primary">
                    Wir helfen<span className="text-flame">.</span>
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={160}>
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-xl">
                  Geduldige, verständliche IT-Hilfe für <strong className="text-navy">Senioren, Privatleute und Kleinunternehmen</strong> in Waren (Müritz).
                </p>
              </Reveal>
            </div>

            {/* CTA Buttons - Larger */}
            <Reveal delay={240}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-7 text-lg font-bold rounded-full shadow-soft transition-all hover:shadow-lift hover:-translate-y-0.5"
                >
                  Kostenloses Erstgespräch
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>

                <Button
                  onClick={() => document.querySelector("#preise")?.scrollIntoView({ behavior: "smooth" })}
                  variant="outline"
                  className="border-2 border-navy/15 bg-background/70 backdrop-blur hover:border-primary text-navy hover:text-primary px-8 py-7 text-lg font-bold rounded-full transition-all"
                >
                  Betreuungspakete
                </Button>
              </div>
            </Reveal>

            {/* Phone CTA */}
            <Reveal delay={300}>
              <a
                href="tel:+4915128958006"
                className="inline-flex items-center gap-3 text-muted-foreground hover:text-primary text-lg font-semibold transition-all group"
              >
                <div className="p-2.5 rounded-full bg-background border border-border shadow-soft group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                Oder rufen Sie an: 01512 895 800 6
              </a>
            </Reveal>

            {/* Trust Indicator */}
            <Reveal delay={360}>
              <div className="inline-flex items-center gap-4 bg-background/80 backdrop-blur rounded-2xl px-6 py-4 border border-border shadow-soft">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-flame fill-current" />
                  ))}
                </div>
                <div className="h-8 w-px bg-border" />
                <div>
                  <div className="font-bold text-navy">Zufriedene Kunden</div>
                  <div className="text-sm text-muted-foreground">in Waren (Müritz) & Umgebung</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Content - Hero Image */}
          <Reveal delay={200} className="relative">
            {/* Verkaufsschild für Klartext-Check */}
            <SaleTag />

            {/* Background decoration – Leiterbahn-Rahmen */}
            <div className="absolute -top-5 -right-5 w-full h-full rounded-[2rem] border-2 border-flame/30 pointer-events-none" aria-hidden="true">
              <span className="absolute -top-[7px] left-10 h-3 w-3 rounded-full border-2 border-flame/60 bg-background" />
              <span className="absolute -right-[7px] top-16 h-3 w-3 rounded-full border-2 border-flame/60 bg-background" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-2/3 h-2/3 bg-gradient-to-tr from-primary/15 to-accent/10 rounded-[2rem] blur-xl pointer-events-none" aria-hidden="true" />

            <Card className="relative overflow-hidden rounded-[2rem] border border-navy/10 shadow-lift">
              <CardContent className="p-0 relative">
                <img
                  src="https://images.unsplash.com/photo-1618171889969-0feeb769fe78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxJVCUyMHN1cHBvcnQlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc1NjIzNjgzN3ww&ixlib=rb-4.1.0&q=85"
                  alt="IT-Support: Hilfe bei Computer, Smartphone und Tablet"
                  className="h-[400px] lg:h-[500px] w-full object-cover"
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 via-transparent to-transparent" />

                {/* Info Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-background/95 backdrop-blur-md rounded-2xl p-5 shadow-lift border border-border">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center shadow-soft">
                          <Laptop2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="font-bold text-navy text-lg">Klartech Müritz</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5 text-flame" />
                            Waren (Müritz)
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full font-semibold text-sm">
                        <Clock className="h-4 w-4" />
                        Mo-Fr verfügbar
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ModernHero;
